import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ZoomIn } from 'lucide-react';

import { InspectionDefect } from '@/types/InspectionDefect';
import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { imageStorage } from '@/store/idbStorage/imageStorage';
import { AppConstants } from '@/app.constants';
import { Image } from '@/types/Image.type';
import { ImageZoomLightbox, ImageZoomSlide } from '@/features/components/ImageCard/ImageZoomLightbox';

import styles from './DefectImage.module.scss';

export function buildDefectImageSlides(defect: InspectionDefect, zoomTitle: string): ImageZoomSlide[] {
    const separator = AppConstants.IMAGE_URL_SEPARATOR;
    const uploadedUrls = (defect.imageUrls || '')
        .split(separator)
        .map((u) => u.trim())
        .filter(Boolean);
    const newImages = (defect.newImages || []).filter((i) => !i.isDeleted);
    const baseKey = defect.defectId || defect.id || defect.newId || 'defect';
    const slides: ImageZoomSlide[] = [];
    uploadedUrls.forEach((url, i) => {
        slides.push({
            image: {
                id: `${baseKey}-url-${i}`,
                url,
                type: InspectionImageType.DEFECT,
            } as Image,
            typeLabel: zoomTitle,
        });
    });
    newImages.forEach((img) => {
        slides.push({
            image: {
                ...img,
                type: img.type ?? InspectionImageType.DEFECT,
            },
            typeLabel: zoomTitle,
        });
    });
    return slides;
}

interface DefectImageProps {
    defect: InspectionDefect;
    inspectionId: string;
    /** Defect description (e.g. code description); shown as the zoom viewer header title */
    zoomTitle?: string;
    /** When set with `onOpenSharedGallery`, opens parent lightbox at this global slide index (all defects). */
    sharedGalleryStartIndex?: number;
    onOpenSharedGallery?: (globalStartIndex: number) => void;
    alt?: string;
    className?: string;
}

export const DefectImage: React.FC<DefectImageProps> = ({
    defect,
    inspectionId,
    zoomTitle: zoomTitleProp,
    sharedGalleryStartIndex,
    onOpenSharedGallery,
    alt = 'defect',
    className,
}) => {
    const { t } = useTranslation();
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [imageCount, setImageCount] = useState<number>(0);
    const [zoomOpen, setZoomOpen] = useState(false);

    const headerTitle = zoomTitleProp?.trim() || t('defect_images');
    const slides = useMemo(() => buildDefectImageSlides(defect, headerTitle), [defect, headerTitle]);

    useEffect(() => {
        const loadImage = async () => {
            let src: string | null = null;
            const separator = AppConstants.IMAGE_URL_SEPARATOR;

            const uploadedUrls = (defect.imageUrls || '')
                .split(separator)
                .map((u) => u.trim())
                .filter(Boolean);

            const newImages = (defect.newImages || []).filter((i) => !i.isDeleted);
            const totalCount = uploadedUrls.length + newImages.length;
            setImageCount(totalCount);

            if (uploadedUrls.length > 0) {
                src = uploadedUrls[0];
            } else if (newImages.length > 0) {
                const firstImage = newImages[0];
                const fromUrl = firstImage.url?.trim() || firstImage.uploadedUrl?.trim();
                if (fromUrl) {
                    src = fromUrl;
                } else {
                    const idb = await imageStorage.getImage(
                        inspectionId,
                        InspectionImageType.DEFECT,
                        firstImage.id,
                    );
                    src = idb?.trim() ? idb : null;
                }
            }

            setImageSrc(src);
        };

        loadImage();
    }, [defect, inspectionId]);

    const openZoom = useCallback(
        (e: React.MouseEvent | React.KeyboardEvent) => {
            e.stopPropagation();
            if (slides.length === 0) return;
            if (onOpenSharedGallery !== undefined && sharedGalleryStartIndex !== undefined) {
                onOpenSharedGallery(sharedGalleryStartIndex);
            } else {
                setZoomOpen(true);
            }
        },
        [slides.length, onOpenSharedGallery, sharedGalleryStartIndex],
    );

    const closeZoom = useCallback(() => setZoomOpen(false), []);

    const onKeyOpenZoom = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openZoom(e);
        }
    };

    if (!imageSrc || slides.length === 0) {
        return null;
    }

    const useSharedGallery = onOpenSharedGallery !== undefined && sharedGalleryStartIndex !== undefined;

    return (
        <div className={styles.wrap}>
            <div
                className={styles.zoomable}
                role="button"
                tabIndex={0}
                aria-label={t('image_zoom_open', { defaultValue: 'View image larger' })}
                onClick={openZoom}
                onKeyDown={onKeyOpenZoom}
            >
                <img src={imageSrc} alt={alt} className={className ?? styles.thumb} />
                <span className={styles.zoomHintBadge} aria-hidden>
                    <ZoomIn className={styles.zoomHintIcon} size={12} strokeWidth={2.25} />
                </span>
            </div>
            {imageCount > 1 && (
                <span className={styles.countBadge} aria-hidden>
                    +{imageCount - 1}
                </span>
            )}
            {!useSharedGallery && zoomOpen && (
                <ImageZoomLightbox
                    inspectionId={inspectionId}
                    inspectionImageType={InspectionImageType.DEFECT}
                    slides={slides}
                    initialIndex={0}
                    onClose={closeZoom}
                />
            )}
        </div>
    );
};
